import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Trash2 } from "lucide-react";
import { api, absUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice, timeAgo } from "@/lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Admin() {
    const [tab, setTab] = useState("overview");
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [listings, setListings] = useState([]);
    const [posts, setPosts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [a, u, l, p] = await Promise.all([
                    api.get("/admin/analytics"),
                    api.get("/admin/users"),
                    api.get("/admin/listings"),
                    api.get("/admin/posts"),
                ]);
                setStats(a.data);
                setUsers(u.data);
                setListings(l.data);
                setPosts(p.data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, []);

    async function toggleVerify(u) {
        try {
            const { data } = await api.patch(`/admin/users/${u.user_id}/verify`, {
                verified: !u.verified_seller,
            });
            setUsers((cur) => cur.map((x) => (x.user_id === data.user_id ? data : x)));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function setListingStatus(l, status) {
        try {
            const { data } = await api.patch(`/admin/listings/${l.listing_id}/status`, { status });
            setListings((cur) => cur.map((x) => (x.listing_id === l.listing_id ? data : x)));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function delPost(p) {
        if (!window.confirm("Delete this post permanently?")) return;
        await api.delete(`/admin/posts/${p.post_id}`);
        setPosts((cur) => cur.filter((x) => x.post_id !== p.post_id));
    }

    return (
        <div className="min-h-screen bg-onyx-900">
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-hellcat" /> Admin
                    </div>
                    <button
                        onClick={() => navigate("/admin/vinny")}
                        className="btn-red ml-auto rounded-full px-3 py-1.5 text-xs flex items-center gap-1"
                        data-testid="admin-open-scars"
                    >
                        S.C.A.R.S. Control Center
                    </button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 py-5">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-onyx-800 border border-onyx-600">
                        <TabsTrigger value="overview" data-testid="tab-overview">
                            Overview
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users">
                            Users
                        </TabsTrigger>
                        <TabsTrigger value="listings" data-testid="tab-listings">
                            Listings
                        </TabsTrigger>
                        <TabsTrigger value="posts" data-testid="tab-posts">
                            Posts
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {stats &&
                                Object.entries(stats).map(([k, v]) => (
                                    <div key={k} className="surface rounded-md p-4">
                                        <div className="label-caps">{k.replace(/_/g, " ")}</div>
                                        <div className="headline text-3xl mt-1 text-white">{v}</div>
                                    </div>
                                ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="users" className="mt-4">
                        <div className="surface rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-onyx-800 text-onyx-500 label-caps text-[10px]">
                                    <tr>
                                        <th className="text-left p-3">User</th>
                                        <th className="text-left p-3">Email</th>
                                        <th className="text-left p-3">Role</th>
                                        <th className="text-left p-3">Verified</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={u.user_id} className="border-t border-onyx-600">
                                            <td className="p-3 flex items-center gap-2">
                                                {u.avatar_url ? (
                                                    <img
                                                        src={absUrl(u.avatar_url)}
                                                        className="w-7 h-7 rounded-full object-cover"
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="w-7 h-7 rounded-full surface-elevated" />
                                                )}
                                                {u.name}
                                            </td>
                                            <td className="p-3 text-onyx-500">{u.email}</td>
                                            <td className="p-3">
                                                {u.role === "admin" ? (
                                                    <span className="label-caps text-hellcat">Admin</span>
                                                ) : (
                                                    "User"
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <button
                                                    onClick={() => toggleVerify(u)}
                                                    data-testid={`verify-toggle-${u.user_id}`}
                                                    className={`rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-widest ${
                                                        u.verified_seller
                                                            ? "bg-hellcat text-white"
                                                            : "btn-ghost"
                                                    }`}
                                                >
                                                    {u.verified_seller ? "Verified" : "Grant"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="listings" className="mt-4">
                        <div className="space-y-2">
                            {listings.map((l) => (
                                <div
                                    key={l.listing_id}
                                    className="surface rounded-md p-3 flex items-center gap-3"
                                >
                                    {l.photos?.[0] && (
                                        <img
                                            src={absUrl(l.photos[0])}
                                            className="w-16 h-12 object-cover rounded-sm"
                                            alt=""
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="headline text-sm truncate">
                                            {l.year} {l.model_name} {l.trim}
                                        </div>
                                        <div className="text-xs text-onyx-500">
                                            {formatPrice(l.price)} · {l.status} · {timeAgo(l.created_at)}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button
                                            onClick={() => setListingStatus(l, "hidden")}
                                            className="btn-ghost rounded-sm px-2 py-1 text-xs"
                                            data-testid={`hide-listing-${l.listing_id}`}
                                        >
                                            Hide
                                        </button>
                                        <button
                                            onClick={() => setListingStatus(l, "active")}
                                            className="btn-ghost rounded-sm px-2 py-1 text-xs"
                                        >
                                            Activate
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="posts" className="mt-4">
                        <div className="space-y-2">
                            {posts.map((p) => (
                                <div
                                    key={p.post_id}
                                    className="surface rounded-md p-3 flex items-start gap-3"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="headline text-sm">{p.title}</div>
                                        <div className="text-xs text-onyx-500 mt-1 line-clamp-2">
                                            {p.body}
                                        </div>
                                        <div className="text-[10px] text-onyx-500 mt-1 label-caps">
                                            {p.category} · {p.upvotes} upvotes · {p.comment_count} comments
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => delPost(p)}
                                        data-testid={`delete-post-${p.post_id}`}
                                        className="btn-ghost rounded-sm px-2.5 py-2 text-hellcat"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
